import { Lock } from "lucide-react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import type { Permission } from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import AppShell from "@/components/AppShell";
import { EmptyState, Spinner } from "@/components/ui/feedback";
import Audit from "@/pages/Audit";
import Catalog from "@/pages/Catalog";
import Config from "@/pages/Config";
import Dashboard from "@/pages/Dashboard";
import HomeLayout from "@/pages/HomeLayout";
import Kyc from "@/pages/Kyc";
import Login from "@/pages/Login";
import Providers from "@/pages/Providers";
import ServiceArea from "@/pages/ServiceArea";
import ServiceReview from "@/pages/ServiceReview";
import Staff from "@/pages/Staff";
import TicketDetail from "@/pages/TicketDetail";
import Tickets from "@/pages/Tickets";
import Users from "@/pages/Users";

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Spinner className="size-6 text-muted-foreground" />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageSpinner />;
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

/**
 * Hiding a nav item is convenience; this is the actual client-side gate. The
 * server enforces the same permission regardless — deep-linking here only ever
 * produces a friendlier message, never access.
 */
function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: React.ReactNode;
}) {
  const { can } = useAuth();
  if (!can(permission)) {
    return (
      <EmptyState
        icon={Lock}
        title="You don't have access to this area"
        description={`This screen needs the "${permission}" permission. Ask an administrator if you think you should have it.`}
      />
    );
  }
  return <>{children}</>;
}

export default function App() {
  const { isAuthenticated, loading } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={
          loading ? (
            <FullPageSpinner />
          ) : isAuthenticated ? (
            <Navigate to="/" replace />
          ) : (
            <Login />
          )
        }
      />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Dashboard />} />
        <Route
          path="tickets"
          element={
            <RequirePermission permission="ticket.read">
              <Tickets />
            </RequirePermission>
          }
        />
        <Route
          path="tickets/:ticketId"
          element={
            <RequirePermission permission="ticket.read">
              <TicketDetail />
            </RequirePermission>
          }
        />
        <Route
          path="users"
          element={
            <RequirePermission permission="user.read">
              <Users />
            </RequirePermission>
          }
        />
        <Route
          path="providers"
          element={
            <RequirePermission permission="provider.read">
              <Providers />
            </RequirePermission>
          }
        />
        <Route
          path="kyc"
          element={
            <RequirePermission permission="kyc.read">
              <Kyc />
            </RequirePermission>
          }
        />
        <Route
          path="service-review"
          element={
            <RequirePermission permission="service.review">
              <ServiceReview />
            </RequirePermission>
          }
        />
        <Route
          path="catalog"
          element={
            <RequirePermission permission="catalog.write">
              <Catalog />
            </RequirePermission>
          }
        />
        <Route
          path="home-layout"
          element={
            <RequirePermission permission="home_layout.write">
              <HomeLayout />
            </RequirePermission>
          }
        />
        <Route
          path="config"
          element={
            <RequirePermission permission="config.read">
              <Config />
            </RequirePermission>
          }
        />
        <Route
          path="service-area"
          element={
            <RequirePermission permission="config.read">
              <ServiceArea />
            </RequirePermission>
          }
        />
        <Route
          path="staff"
          element={
            <RequirePermission permission="staff.manage">
              <Staff />
            </RequirePermission>
          }
        />
        <Route
          path="audit"
          element={
            <RequirePermission permission="audit.read">
              <Audit />
            </RequirePermission>
          }
        />
        <Route
          path="*"
          element={
            <EmptyState
              title="Page not found"
              description="That screen doesn't exist in the console."
            />
          }
        />
      </Route>
    </Routes>
  );
}
